import * as Sentry from '@sentry/browser';
import { HttpClient } from '@sentry/integrations';
import { Environment } from './Environment';

function environmentName(): string {
  let name = 'development';

  if (Environment.STAGING) {
    name = 'staging';
  } else if (Environment.PRODUCTION) {
    name = 'production';
  }

  return name;
}

/**
 * Initialize Sentry client only for staging and production environments
 */
function initialize(): void {
  if (!Environment.DEVELOPMENT) {
    Sentry.init({
      dsn: 'https://d90115cf1e554bd68a640d65ceb6eeac@sentry.io/3398115',
      environment: environmentName(),
      release: `${Environment.APP}@${Environment.VERSION}`,
      ...sentryIntegrations(),
      beforeSend(event) {
        if (event.user) {
          // Don't send user's email address
          delete event.user.email;
        }

        // Filter out errors from Facebook's in-app browser autofill bridge
        const frames = event.exception?.values?.[0]?.stacktrace?.frames;
        if (
          frames?.some((frame) => frame.function?.includes('setContactAutofillValuesFromBridge'))
        ) {
          return null;
        }

        return event;
      },
      // List of ignored Remote config values
      // https://mindsethealth.atlassian.net/browse/PRO-4273
      ignoreErrors: [
        'Error thrown when opening storage.',
        'Error thrown when writing to storage.',
        "Failed to execute 'createObjectStore' on 'IDBDatabase'",
        "Failed to execute 'transaction' on 'IDBDatabase'",
        'The config fetch request timed out.',
      ],
    });
    Sentry.setTag('app', Environment.APP);
  }
}

/**
 * Enable browser tracing and other Sentry integrations
 */
function sentryIntegrations(): Sentry.BrowserOptions {
  return {
    integrations: [new Sentry.BrowserTracing(), new HttpClient()],
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 0.1,
  };
}

/**
 * Wrapper for Sentry.captureException() that wraps non-errors and logs to console
 * @param error - Reason for the error, e.g. from a catch block. Caller should pass an instance of Error
 *          where possible for the most useful stack trace.
 */
export function recordError(error: Error | unknown): void {
  console.error(`recordError(${JSON.stringify(error)})`, error);

  if (error instanceof Error) {
    // Use original error if possible to preserve the stack trace
    Sentry.captureException(error);
  } else {
    Sentry.captureException(new Error(`${JSON.stringify(error)}`));
  }
}

export const Errors = {
  initialize,
};
